// Name & Brand Numerology Comparison Engine (अंक ज्योतिष नाम व ब्रांड तुलना)
// Supporting Chaldean, Pythagorean, Astrological Synergy, Domain Parsing, and Practical Brand Analytics.

import { CompleteKundali } from '../types/kundali';
import { reduceToSingleDigit, NUMBER_LORDS, NUMBER_RELATIONS, CHALDEAN_MAP, PYTHAGOREAN_MAP } from './numerologyEngine';

export type NumerologySystem = 'CHALDEAN' | 'PYTHAGOREAN';

export type BrandPurpose =
  | 'ASTROLOGY_APP'
  | 'ASTROLOGY_PRACTICE'
  | 'PERSONAL_BRAND'
  | 'BUSINESS'
  | 'PRODUCT'
  | 'WEBSITE'
  | 'DOMAIN'
  | 'COMPANY'
  | 'OTHER';

export interface CompoundNumberData {
  number: number;
  singleDigit: number;
  symbolName: string;
  symbolNameHi: string;
  nature: 'HIGHLY_AUSPICIOUS' | 'AUSPICIOUS' | 'CHALLENGING' | 'VOLATILE' | 'NEUTRAL';
  meaning: string;
  meaningHi: string;
  commercialSuitability: string;
  commercialSuitabilityHi: string;
}

// Classical Chaldean & Cheiro Compound Number Meanings (10 to 70+)
export const COMPOUND_NUMBERS_DATABASE: Record<number, CompoundNumberData> = {
  10: {
    number: 10,
    singleDigit: 1,
    symbolName: 'Wheel of Fortune (भाग्य चक्र)',
    symbolNameHi: 'भाग्य का पहिया - सम्मान व उन्नति',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Symbol of honor, faith, self-confidence, and rising fortune. Known as the number of new cycles and independent leadership.',
    meaningHi: 'सम्मान, आत्मविश्वास, और निरंतर उत्थान का प्रतीक। नए आरंभ और स्वतंत्र नेतृत्व के लिए श्रेष्ठ।',
    commercialSuitability: 'Exceptional for flagship tech brands, pioneering apps, and executive business entities.',
    commercialSuitabilityHi: 'प्रमुख तकनीकी ब्रांडों, अग्रणी ऐप्स और कॉरपोरेट कंपनियों के लिए अत्यंत लाभकारी।'
  },
  11: {
    number: 11,
    singleDigit: 2,
    symbolName: 'The Clenched Fist / Master Intuition',
    symbolNameHi: 'मास्टर अंतर्ज्ञान - आध्यात्मिक शक्ति व चेतावनी',
    nature: 'VOLATILE',
    meaning: 'A Master Number representing high spiritual intuition and inspiration, but requires caution against hidden deception or emotional stress.',
    meaningHi: 'मास्टर नंबर जो उच्च आध्यात्मिक अंतर्ज्ञान का प्रतीक है, किंतु गुप्त विरोध और तनाव से सचेत रहने का संकेत देता है।',
    commercialSuitability: 'Strong for spiritual guidance, psychology, and counseling; requires steady management in commercial apps.',
    commercialSuitabilityHi: 'आध्यात्मिक मार्गदर्शन और परामर्श के लिए उत्तम; वाणिज्यिक व्यवसाय में सतर्क प्रबंधन आवश्यक।'
  },
  12: {
    number: 12,
    singleDigit: 3,
    symbolName: 'The Sacrifice / The Teacher',
    symbolNameHi: 'त्याग व विद्या - ज्ञान का प्रसार',
    nature: 'NEUTRAL',
    meaning: 'Represents deep learning, altruistic teaching, and wisdom acquired through dedicated service and personal experience.',
    meaningHi: 'गहन ज्ञान, निःस्वार्थ शिक्षण और सेवा के माध्यम से अर्जित अनुभव का प्रतीक।',
    commercialSuitability: 'Excellent for educational academies, non-profits, and traditional literature.',
    commercialSuitabilityHi: 'शिक्षण संस्थानों, शोध और पारंपरिक साहित्य प्रकाशन के लिए उपयुक्त।'
  },
  13: {
    number: 13,
    singleDigit: 4,
    symbolName: 'Regeneration & Transformation',
    symbolNameHi: 'पुनर्जन्म व रूपांतरण - क्रांतिकारी बदलाव',
    nature: 'VOLATILE',
    meaning: 'Karmic number of sudden change, power shifts, and breaking obsolete structures to forge new pathways.',
    meaningHi: 'अचानक परिवर्तन, शक्ति-रूपांतरण और पुरानी सीमाओं को तोड़कर नई व्यवस्था बनाने का अंक।',
    commercialSuitability: 'Suits disruptive technology startups, cybersecurity, and niche research.',
    commercialSuitabilityHi: 'क्रांतिकारी तकनीकी स्टार्टअप और गहन अनुसंधान के लिए उपयोगी।'
  },
  14: {
    number: 14,
    singleDigit: 5,
    symbolName: 'Movement of the Winds (गति व वाणिज्य)',
    symbolNameHi: 'पवन गति - व्यापार, संचार व गतिशीलता',
    nature: 'AUSPICIOUS',
    meaning: 'Represents rapid communication, international commerce, travel, versatility, and adaptability.',
    meaningHi: 'तीव्र संवाद, अंतर्राष्ट्रीय व्यापार, यात्रा और बहुमुखी प्रतिभा का शुभ प्रतीक।',
    commercialSuitability: 'Very favorable for digital media, communication tools, travel portals, and global platforms.',
    commercialSuitabilityHi: 'डिजिटल मीडिया, संचार उपकरण, यात्रा और वैश्विक प्लेटफॉर्म के लिए अत्यंत शुभ।'
  },
  15: {
    number: 15,
    singleDigit: 6,
    symbolName: 'The Magician / Magnetic Charm',
    symbolNameHi: 'जादुई आकर्षण - सम्मोहन, कला व धन',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'The number of mystery, artistic magic, eloquence, financial magnetism, and overwhelming public appeal.',
    meaningHi: 'कलात्मक सम्मोहन, मधुर वाणी, प्रचुर धन आकर्षण और जनप्रियता का महा-शुभ अंक।',
    commercialSuitability: 'Superb for astrology apps, music, luxury brands, creative platforms, and mass consumer products.',
    commercialSuitabilityHi: 'ज्योतिष ऐप, संगीत, लक्जरी ब्रांड और जन-उपयोगी उत्पादों के लिए अत्यंत फलदायी।'
  },
  16: {
    number: 16,
    singleDigit: 7,
    symbolName: 'The Shattered Citadel (कठिन आत्म-मंथन)',
    symbolNameHi: 'चुनौतीपूर्ण अहंकार नाश - आध्यात्मिक शोध',
    nature: 'CHALLENGING',
    meaning: 'A karmic debt number warning against pride or sudden unforeseen disruptions; drives deep spiritual awakening.',
    meaningHi: 'अहंकार से बचने और अप्रत्याशित उतार-चढ़ाव की चेतावनी देने वाला अंक; आध्यात्मिक गहराई देता है।',
    commercialSuitability: 'Not recommended for standard commercial brand names; best suited for ascetic spiritual pursuits.',
    commercialSuitabilityHi: 'व्यावसायिक ब्रांड के लिए अनुशंसित नहीं; केवल गहन आध्यात्मिक शोध के लिए उपयुक्त।'
  },
  17: {
    number: 17,
    singleDigit: 8,
    symbolName: 'The Star of the Magi (ज्ञान का सितारा)',
    symbolNameHi: 'ज्ञान का चमकता सितारा - अमर कीर्ति व विजय',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Known in classical Chaldean as "The Star of the Magi". Bestows immortal fame, enduring success, spiritual elevation, and invincibility.',
    meaningHi: 'चैल्डियन ज्योतिष का अत्यंत पवित्र अंक। अमर यश, स्थायी सफलता, आध्यात्मिक विजय और अटूट प्रतिष्ठा प्रदान करता है।',
    commercialSuitability: 'Exceptional for prestigious spiritual organizations, Vedic platforms, publishing, and legacy brands.',
    commercialSuitabilityHi: 'वैदिक प्लेटफॉर्म, ज्योतिष शोध, प्रतिष्ठित प्रकाशन और दीर्घकालिक ब्रांड्स के लिए सर्वोत्कृष्ट।'
  },
  18: {
    number: 18,
    singleDigit: 9,
    symbolName: 'Spiritual Conflict & Inner Trial',
    symbolNameHi: 'द्वंद्व व परीक्षा - आंतरिक संघर्ष',
    nature: 'CHALLENGING',
    meaning: 'Represents intense trials, internal friction, deception from close associates, and spiritual purification through battle.',
    meaningHi: 'संघर्ष, षड्यंत्रों से बचाव और कड़े परिश्रम से शुद्धि का अंक।',
    commercialSuitability: 'Requires caution in business and partnerships; needs protective balancing factors.',
    commercialSuitabilityHi: 'साझेदारी और सामान्य व्यवसाय में सावधानी की आवश्यकता।'
  },
  19: {
    number: 19,
    singleDigit: 1,
    symbolName: 'The Prince of Heaven / Radiant Sun',
    symbolNameHi: 'स्वर्ग का राजकुमार - सूर्य का तेज व विजय',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'One of the most fortunate compound numbers. Symbolizes boundless solar energy, victory, happiness, and supreme professional success.',
    meaningHi: 'अंक शास्त्र का सर्वोत्कृष्ट विजयी अंक। सूर्य का प्रचंड तेज, निरंतर उन्नति, सुख और उच्च सफलता का द्योतक।',
    commercialSuitability: 'Outstanding for high-growth tech enterprises, leadership platforms, and flagship applications.',
    commercialSuitabilityHi: 'शीर्ष तकनीकी कंपनियों, नेतृत्व प्लेटफॉर्म और प्रमुख मोबाइल ऐप्स के लिए अत्यंत शुभ।'
  },
  20: {
    number: 20,
    singleDigit: 2,
    symbolName: 'The Awakening (पुनर्जागरण)',
    symbolNameHi: 'आध्यात्मिक जागृति - नई दृष्टि व शांति',
    nature: 'AUSPICIOUS',
    meaning: 'Represents awakening to a higher purpose, artistic diplomacy, noble pursuits, and peaceful transformation.',
    meaningHi: 'उच्च उद्देश्य की प्राप्ति, कलात्मक कूटनीति, शांति और नई दृष्टि का परिचायक।',
    commercialSuitability: 'Good for counseling services, wellness hubs, and artistic communities.',
    commercialSuitabilityHi: 'स्वास्थ्य, परामर्श और कलात्मक मंचों के लिए श्रेष्ठ।'
  },
  21: {
    number: 21,
    singleDigit: 3,
    symbolName: 'The Crown of the Magi (विजय का मुकुट)',
    symbolNameHi: 'विजय का मुकुट - वैश्विक सफलता व विस्तार',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Symbol of worldwide recognition, ultimate triumph after determined effort, scholarly honor, and divine blessings.',
    meaningHi: 'वैश्विक प्रतिष्ठा, दृढ़ प्रयासों के बाद अंतिम विजय और देव कृपा का महा-शुभ अंक।',
    commercialSuitability: 'Prime number for international astrology portals, global academic institutions, and enterprise software.',
    commercialSuitabilityHi: 'अंतर्राष्ट्रीय ज्योतिष पोर्टलों, वैश्विक शैक्षणिक संस्थानों और सॉफ्टवेयर उत्पादों के लिए सर्वोत्तम।'
  },
  22: {
    number: 22,
    singleDigit: 4,
    symbolName: 'The Master Builder (महान निर्माता)',
    symbolNameHi: 'मास्टर बिल्डर - वैश्विक निर्माण व व्यावहारिकता',
    nature: 'AUSPICIOUS',
    meaning: 'Master Number 22. Endowed with the capacity to manifest grand visions into physical reality on a massive scale.',
    meaningHi: 'मास्टर नंबर 22। भव्य सपनों को धरातल पर विशाल स्तर पर साकार करने की अद्वितीय क्षमता।',
    commercialSuitability: 'Superb for architecture, global infrastructure, scalable digital platforms, and major organizations.',
    commercialSuitabilityHi: 'विशाल डिजिटल प्लेटफॉर्म, बुनियादी ढांचे और वैश्विक संगठनों के लिए श्रेष्ठ।'
  },
  23: {
    number: 23,
    singleDigit: 5,
    symbolName: 'The Royal Star of the Lion (सिंह का राज-सितारा)',
    symbolNameHi: 'सिंह का राज-सितारा - सर्वप्रिय, तीव्र सफलता व धन',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'One of the greatest commercial and brand numbers in existence. Bestows unstoppable charm, success with superiors, rapid wealth, and widespread popularity.',
    meaningHi: 'व्यापार व ब्रांडिंग का सर्वाधिक भाग्यशाली अंक। अपार आकर्षण, उच्च अधिकारियों का सहयोग, त्वरित धन लाभ और अपार लोकप्रियता।',
    commercialSuitability: 'THE GOLD STANDARD for mobile apps, Vedic platforms, media, commercial ventures, and modern consumer brands.',
    commercialSuitabilityHi: 'मोबाइल ऐप्स, वैदिक प्लेटफॉर्म्स, मीडिया और आधुनिक उपभोक्ता ब्रांड्स के लिए स्वर्ण मानक (Gold Standard)।'
  },
  24: {
    number: 24,
    singleDigit: 6,
    symbolName: 'Love, Money & Powerful Alliances',
    symbolNameHi: 'प्रेम, वैभव व शक्तिशाली संरक्षण',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Guarantees the assistance of powerful people, sustained prosperity, artistic harmony, and steady financial growth.',
    meaningHi: 'प्रभावशाली व्यक्तियों का संरक्षण, निरंतर धन लाभ, कलात्मक संतुलन और स्थिर प्रगति सुनिश्चित करता है।',
    commercialSuitability: 'Ideal for astrology consulting, luxury lifestyle apps, boutique agencies, and wealth management.',
    commercialSuitabilityHi: 'ज्योतिष परामर्श, लक्जरी जीवनशैली, बुटीक सेवाओं और वित्तीय सलाह के लिए उत्तम।'
  },
  25: {
    number: 25,
    singleDigit: 7,
    symbolName: 'Discrimination & Analysis (विवेक व शोध)',
    symbolNameHi: 'विवेक व शोध - अनुभव से अर्जित सिद्धि',
    nature: 'AUSPICIOUS',
    meaning: 'Represents deep analytical research, spiritual wisdom gained through trials, and discerning intellectual intellect.',
    meaningHi: 'गहन विश्लेषणात्मक शोध, अनुभवों से परिपक्व ज्ञान और तीक्ष्ण बौद्धिक विवेक का प्रतीक।',
    commercialSuitability: 'Great for research engines, analytical AI platforms, data science, and occult philosophy.',
    commercialSuitabilityHi: 'शोध इंजनों, विश्लेषणात्मक एआई, डेटा साइंस और गूढ़ दर्शन के लिए उपयुक्त।'
  },
  26: {
    number: 26,
    singleDigit: 8,
    symbolName: 'Partnerships & Karmic Balance',
    symbolNameHi: 'साझेदारी व कर्मफल - सतर्क प्रबंधन',
    nature: 'NEUTRAL',
    meaning: 'Represents great organizational capacity, but warns of partnership disputes and financial liabilities if unchecked.',
    meaningHi: 'उत्कृष्ट संगठन क्षमता, किंतु अनियंत्रित होने पर साझेदारी में मतभेद व वित्तीय सतर्कता का संकेत।',
    commercialSuitability: 'Viable with single-owner structures and strong legal contracts; avoid in chaotic partnerships.',
    commercialSuitabilityHi: 'एकल स्वामित्व और मजबूत कानूनी व्यवस्था वाले उपक्रमों के लिए ठीक।'
  },
  27: {
    number: 27,
    singleDigit: 9,
    symbolName: 'The Sceptre (आदेश दंड व सत्ता)',
    symbolNameHi: 'राजदंड - नेतृत्व, अधिकार व पराक्रम',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'A formidable number of supreme authority, executive command, intellectual courage, and military/administrative precision.',
    meaningHi: 'सर्वोच्च प्रशासनिक अधिकार, बौद्धिक साहस, नेतृत्व और पराक्रम का अत्यंत शुभ अंक।',
    commercialSuitability: 'Excellent for governance platforms, commanding publications, leadership tools, and defense/legaltech.',
    commercialSuitabilityHi: 'प्रशासनिक सॉफ्टवेयर, कानूनी तकनीक और नेतृत्व पोर्टलों के लिए अत्यंत शुभ।'
  },
  28: {
    number: 28,
    singleDigit: 1,
    symbolName: 'The Trusting Mind (विश्वास व सावधानी)',
    symbolNameHi: 'विश्वास व सावधानी - उतार-चढ़ाव की चेतावनी',
    nature: 'NEUTRAL',
    meaning: 'Full of great promise and initial success, but warns against blind trust in business contracts or unexpected competitors.',
    meaningHi: 'प्रारंभिक सफलता का योग, किंतु अनुबंधों में अत्यधिक विश्वास और अचानक प्रतिस्पर्धियों से सतर्क रहने की चेतावनी।',
    commercialSuitability: 'Requires rigorous trademarking, strict risk controls, and clear governance.',
    commercialSuitabilityHi: 'कड़े जोखिम नियंत्रण और स्पष्ट कानूनी प्रक्रियाओं के साथ ही अनुशंसित।'
  },
  29: {
    number: 29,
    singleDigit: 2,
    symbolName: 'Grace under Pressure (धैर्य व परीक्षण)',
    symbolNameHi: 'धैर्य व परीक्षा - अनिश्चितता व साधना',
    nature: 'CHALLENGING',
    meaning: 'Represents severe mental anxieties, sudden uncertainties in alliances, but profound psychic awakening through resilience.',
    meaningHi: 'मानसिक तनाव, अनिश्चितता किंतु आत्मबल से उबरने पर गूढ़ ज्ञान की प्राप्ति।',
    commercialSuitability: 'Not ideal for fast-moving commercial consumer brands; requires name tuning if possible.',
    commercialSuitabilityHi: 'वाणिज्यिक उपभोक्ता ब्रांडों के लिए बहुत अनुकूल नहीं; नामांक संशोधन बेहतर।'
  },
  30: {
    number: 30,
    singleDigit: 3,
    symbolName: 'The Luminous Sage (ज्ञान प्रकाश)',
    symbolNameHi: 'ज्ञान प्रकाश - विद्वता, दर्शन व मानसिक श्रेष्ठता',
    nature: 'AUSPICIOUS',
    meaning: 'A brilliant mental and scholarly number. Outshines others in writing, literature, philosophy, and intellectual creation.',
    meaningHi: 'विद्वता, उच्च साहित्य, दर्शन और मानसिक प्रतिभा में दूसरों से आगे रहने वाला उत्तम अंक।',
    commercialSuitability: 'Highly recommended for astrology journals, publishing houses, scholarly software, and spiritual content.',
    commercialSuitabilityHi: 'ज्योतिष पत्रिकाओं, प्रकाशन गृहों और आध्यात्मिक सामग्री के लिए अत्यंत उपयुक्त।'
  },
  31: {
    number: 31,
    singleDigit: 4,
    symbolName: 'The Solitary Thinker (एकांत विचारक)',
    symbolNameHi: 'एकांत विचारक - मौलिकता व तकनीकी एकाग्रता',
    nature: 'NEUTRAL',
    meaning: 'Original, self-contained, intellectual, but prone to self-isolation and aloofness from commercial trends.',
    meaningHi: 'मौलिक तकनीकी सोच, किंतु व्यावसायिक प्रवृत्तियों से थोड़ा अलग-थलग रहने की संभावना।',
    commercialSuitability: 'Good for niche developer tools, open-source libraries, and deep research laboratories.',
    commercialSuitabilityHi: 'डेवलपर टूल्स, ओपन सोर्स और अनुसंधान प्रयोगशालाओं के लिए ठीक।'
  },
  32: {
    number: 32,
    singleDigit: 5,
    symbolName: 'The Magic of Nations (वैश्विक लोकप्रियता)',
    symbolNameHi: 'वैश्विक लोकप्रियता - जनसंचार व व्यापारिक जादू',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Similar to number 23, holds magnetic sway over large groups, mass media, versatile commerce, and quick growth.',
    meaningHi: 'अंक 23 की भांति विशाल जनसमूह पर प्रभाव, मीडिया सफलता और त्वरित व्यापारिक विस्तार का महा-शुभ अंक।',
    commercialSuitability: 'Top-tier for mobile apps, interactive platforms, e-commerce, and widespread consumer brands.',
    commercialSuitabilityHi: 'मोबाइल ऐप्स, ई-कॉमर्स और जनसंचार ब्रांड्स के लिए शीर्ष श्रेणी का अंक।'
  },
  33: {
    number: 33,
    singleDigit: 6,
    symbolName: 'The Master Teacher of Light (महा-गुरु व ऐश्वर्य)',
    symbolNameHi: 'महा-गुरु व ऐश्वर्य - दिव्य ज्ञान, प्रेम व समृद्धि',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Highest of the Master numbers. Symbol of selfless love, spiritual illumination, boundless creative magnetism, and divine fortune.',
    meaningHi: 'सर्वोच्च मास्टर नंबर। दिव्य ज्ञान, निःस्वार्थ सेवा, रचनात्मक सम्मोहन और असीम ऐश्वर्य का मंगलकारी योग।',
    commercialSuitability: 'MAGNIFICENT for Vedic astrology platforms, spiritual wellness applications, and holistic lifestyle brands.',
    commercialSuitabilityHi: 'वैदिक ज्योतिष प्लेटफार्मों, आध्यात्मिक कल्याण ऐप्स और समग्र जीवनशैली ब्रांडों के लिए सर्वोत्तम।'
  },
  34: {
    number: 34,
    singleDigit: 7,
    symbolName: 'The Structured Seeker',
    symbolNameHi: 'अनुशासित साधक - क्रमबद्ध शोध व अध्यात्म',
    nature: 'AUSPICIOUS',
    meaning: 'Combines Jupiter (3) and Rahu (4) reducing to Ketu (7). Deep systematic research, precision, and occult depth.',
    meaningHi: 'क्रमबद्ध शोध, गणितीय सटीकता और आध्यात्मिक गहराई का सुंदर संगम।',
    commercialSuitability: 'Very good for algorithmic astrology engines, precision software, and data analytics.',
    commercialSuitabilityHi: 'सटीक गणनात्मक सॉफ्टवेयर और डेटा एनालिटिक्स के लिए बहुत अच्छा।'
  },
  35: {
    number: 35,
    singleDigit: 8,
    symbolName: 'The Material Strategist',
    symbolNameHi: 'व्यावहारिक रणनीतिकार - अनुभव व संपदा',
    nature: 'NEUTRAL',
    meaning: 'Combines wisdom (3) and communication (5) with Saturn (8). Steady wealth accumulation through careful enterprise.',
    meaningHi: 'सावधानीपूर्वक किए गए उद्यमों से धीरे-धीरे स्थायी संपदा का निर्माण।',
    commercialSuitability: 'Suitable for financial advisory, accounting platforms, and asset management.',
    commercialSuitabilityHi: 'वित्तीय परामर्श और संपत्ति प्रबंधन के लिए उपयुक्त।'
  },
  36: {
    number: 36,
    singleDigit: 9,
    symbolName: 'The Humanitarian Champion',
    symbolNameHi: 'परोपकारी विजेता - कला व जन-सेवा',
    nature: 'AUSPICIOUS',
    meaning: 'Combines Jupiter (3) and Venus (6) ending in Mars (9). Unites wisdom and artistic charm for philanthropic triumph.',
    meaningHi: 'ज्ञान और कला का संगम जो जन-कल्याण और प्रतिष्ठा में वृद्धि करता है।',
    commercialSuitability: 'Great for humanitarian apps, cultural heritage platforms, and educational organizations.',
    commercialSuitabilityHi: 'सांस्कृतिक विरासत और सामाजिक संस्थाओं के लिए उत्तम।'
  },
  37: {
    number: 37,
    singleDigit: 1,
    symbolName: 'The Royal Harmony of Love & Fortune',
    symbolNameHi: 'प्रेम व सौभाग्य का राजयोग - निरंतर यश व समृद्धि',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'One of the most blessed compound numbers. Combines Jupiter (3) and Ketu (7) to form a majestic Sun (1). Pure good fortune, friendship, and success in partnerships.',
    meaningHi: 'अत्यंत शुभ अंक। गुरु (3) और केतु (7) के योग से बना तेजस्वी सूर्य (1)। जीवन और व्यापार में अखंड सौभाग्य और लोकप्रियता।',
    commercialSuitability: 'PERFECT for partnerships, consulting firms, astrology apps, and luxury consumer enterprises.',
    commercialSuitabilityHi: 'साझेदारी, परामर्श, ज्योतिष ऐप्स और प्रतिष्ठित उपभोक्ता उद्यमों के लिए एकदम उपयुक्त।'
  },
  41: {
    number: 41,
    singleDigit: 5,
    symbolName: 'The Dynamic Catalyst',
    symbolNameHi: 'गतिशील उत्प्रेरक - व्यापार, गति व आकर्षण',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Similar to 14 and 23. Powerful commercial vibration, quick problem solving, high charm, and financial success.',
    meaningHi: 'व्यापार में तेजी, त्वरित समस्या समाधान और वित्तीय सफलता का अत्यंत शुभ अंक।',
    commercialSuitability: 'Excellent for high-speed technology, modern apps, fintech, and digital platforms.',
    commercialSuitabilityHi: 'आधुनिक तकनीक, फिनटेक और डिजिटल ऐप्स के लिए अत्यंत लाभकारी।'
  },
  42: {
    number: 42,
    singleDigit: 6,
    symbolName: 'The Harmonious Foundation',
    symbolNameHi: 'सुदृढ़ संतुलन - ऐश्वर्य व कला',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Brings immense love, beauty, domestic and corporate peace, stable luxury, and long-term patron support.',
    meaningHi: 'अपार शांति, सौंदर्य, स्थिर संपन्नता और दीर्घकालिक ग्राहकों का विश्वास दिलाने वाला शुभ अंक।',
    commercialSuitability: 'Superb for lifestyle apps, wellness, astrology consulting, and luxury goods.',
    commercialSuitabilityHi: 'लाइफस्टाइल ऐप्स, स्वास्थ्य, ज्योतिष और लक्जरी उत्पादों के लिए उत्कृष्ट।'
  },
  45: {
    number: 45,
    singleDigit: 9,
    symbolName: 'The Global Visionary',
    symbolNameHi: 'वैश्विक दूरदर्शी - शक्ति, साहस व ख्याति',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Combines structural planning (4) with agile intellect (5) to generate triumphant Mars (9). Monumental public reach.',
    meaningHi: 'योजना और बुद्धि का ऐसा संगम जो वैश्विक स्तर पर ख्याति और विजय दिलाता है।',
    commercialSuitability: 'Top tier for international portals, tech scale-ups, and global campaigns.',
    commercialSuitabilityHi: 'अंतर्राष्ट्रीय पोर्टलों और बड़े स्तर के अभियानों के लिए श्रेष्ठ।'
  },
  46: {
    number: 46,
    singleDigit: 1,
    symbolName: 'The Sovereign Architect',
    symbolNameHi: 'सार्वभौमिक निर्माता - स्थायी अधिकार व तेज',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Brings unstoppable authority, high reputation, wealth, and continuous ascendancy through structured brilliance.',
    meaningHi: 'अखंड अधिकार, उच्च प्रतिष्ठा, धन और निरंतर उन्नति प्रदान करने वाला राजसी अंक।',
    commercialSuitability: 'Prime for enterprise solutions, prominent brands, and industry leaders.',
    commercialSuitabilityHi: 'कॉरपोरेट सॉल्युशन और उद्योग के शीर्ष ब्रांड्स के लिए अत्यंत उत्तम।'
  },
  50: {
    number: 50,
    singleDigit: 5,
    symbolName: 'The Cosmic Communicator',
    symbolNameHi: 'ब्रह्मांडीय संचारक - वैश्विक स्वतंत्रता व व्यापार',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'Zero amplifies the boundless commercial versatility of Mercury (5). Great intelligence, rapid speed, and adaptability.',
    meaningHi: 'बुध (5) की व्यापारिक गति का विस्तार। कुशाग्र बुद्धि, त्वरित गति और वैश्विक अनुकूलन।',
    commercialSuitability: 'Exceptional for mass SaaS, social apps, AI assistants, and international trade.',
    commercialSuitabilityHi: 'मास सास (SaaS), सोशल ऐप्स, एआई असिस्टेंट और वैश्विक व्यापार के लिए सर्वोत्तम।'
  },
  51: {
    number: 51,
    singleDigit: 6,
    symbolName: 'The Royal Commander of Fortune',
    symbolNameHi: 'सौभाग्य का सेनापति - असाधारण सफलता व नेतृत्व',
    nature: 'HIGHLY_AUSPICIOUS',
    meaning: 'A powerhouse number of extraordinary good fortune, triumph in competition, high status, and financial mastery.',
    meaningHi: 'प्रतियोगिता में विजय, उच्च सामाजिक पद और वित्तीय संपन्नता का अद्भुत शक्तिशाली अंक।',
    commercialSuitability: 'Outstanding for leading astrology software, premium apps, and powerhouse commercial brands.',
    commercialSuitabilityHi: 'प्रमुख ज्योतिष सॉफ्टवेयर, प्रीमियम ऐप्स और शक्तिशाली वाणिज्यिक ब्रांडों के लिए सर्वोत्कृष्ट।'
  },
  52: {
    number: 52,
    singleDigit: 7,
    symbolName: 'The Analytical Seeker',
    symbolNameHi: 'विश्लेषक अन्वेषक - गहन ज्ञान व शोध',
    nature: 'AUSPICIOUS',
    meaning: 'Deep investigative acumen, spiritual insight, analytical depth, and mastery of hidden sciences.',
    meaningHi: 'गहन खोज, आध्यात्मिक अंतर्दृष्टि और गुप्त विज्ञानों में महारत का द्योतक।',
    commercialSuitability: 'Excellent for algorithmic computing, astronomical data, and deep Vedic research.',
    commercialSuitabilityHi: 'एल्गोरिद्मिक कंप्यूटिंग, खगोल विज्ञान और वैदिक शोध के लिए उत्तम।'
  }
};

// Fallback generator for unlisted compound numbers
export function getCompoundNumberData(compound: number): CompoundNumberData {
  if (compound <= 0) {
    return {
      number: 0,
      singleDigit: 0,
      symbolName: 'Unassigned (No Latin Characters)',
      symbolNameHi: 'अनिर्धारित (कोई लैटिन वर्ण नहीं)',
      nature: 'NEUTRAL',
      meaning: 'No Latin alphabet characters detected for Chaldean evaluation.',
      meaningHi: 'चालडियन मूल्यांकन के लिए कोई अंग्रेजी वर्णमाला वर्ण नहीं मिला।',
      commercialSuitability: 'Input brand name using standard Latin transliteration.',
      commercialSuitabilityHi: 'मानक लैटिन लिप्यंतरण का उपयोग करके ब्रांड नाम दर्ज करें।'
    };
  }

  if (COMPOUND_NUMBERS_DATABASE[compound]) {
    return COMPOUND_NUMBERS_DATABASE[compound];
  }
  const single = reduceToSingleDigit(compound) || 1;
  const lord = NUMBER_LORDS[single]?.en || 'Planetary Energy';
  const lordHi = NUMBER_LORDS[single]?.hi || 'ग्रह ऊर्जा';
  
  // Auspicious single digits for business: 1, 3, 5, 6
  const isAuspicious = [1, 3, 5, 6, 9].includes(single);

  return {
    number: compound,
    singleDigit: single,
    symbolName: `Vibrational Matrix ${compound} (${lord})`,
    symbolNameHi: `कंपन स्वरूप ${compound} (${lordHi})`,
    nature: isAuspicious ? 'AUSPICIOUS' : 'NEUTRAL',
    meaning: `Combines dual digits to express the core energy of ${lord}. Promotes steady focus and systematic manifestation.`,
    meaningHi: `दोहरे अंकों के संयोजन से ${lordHi} की ऊर्जा व्यक्त होती है। यह निरंतर एकाग्रता और कार्य सिद्धि को बढ़ावा देता है।`,
    commercialSuitability: isAuspicious 
      ? 'Favorable for general enterprise and digital operations with consistent brand messaging.'
      : 'Viable with clear governance and alignment with founder\'s numerological numbers.',
    commercialSuitabilityHi: isAuspicious 
      ? 'स्थिर ब्रांड संदेश और निरंतर प्रयासों के साथ सामान्य व्यवसाय के लिए अनुकूल।'
      : 'संस्थापक के मूलांक/भाग्यांक के साथ सामंजस्य स्थापित करके उपयोग के लिए उपयुक्त।'
  };
}

export interface LetterBreakdown {
  char: string;
  value: number;
}

export interface NameDecomposition {
  originalInput: string;
  cleanedBrandName: string;
  domainString?: string;
  domainExtension?: string;
  isDomainFormat: boolean;
}

export function parseDomainOrBrand(input: string): NameDecomposition {
  const trimmed = input.trim();
  const domainPattern = /^([a-zA-Z0-9-]+)\.([a-zA-Z]{2,10}(?:\.[a-zA-Z]{2,})?)$/i;
  const match = trimmed.match(domainPattern);

  if (match) {
    const domainBody = match[1];
    const ext = match[2].toLowerCase();
    // Convert camelCase or hyphens to clean brand name if needed
    const brandGuess = domainBody.replace(/[-_]/g, ' ');
    return {
      originalInput: trimmed,
      cleanedBrandName: brandGuess,
      domainString: domainBody.toLowerCase(),
      domainExtension: `.${ext}`,
      isDomainFormat: true
    };
  }

  // Check if extension typed at end with space e.g. "Vedanga Jyotisha .com"
  const spacedExtMatch = trimmed.match(/^(.+?)\s*\.([a-zA-Z]{2,10})$/i);
  if (spacedExtMatch) {
    return {
      originalInput: trimmed,
      cleanedBrandName: spacedExtMatch[1].trim(),
      domainString: spacedExtMatch[1].replace(/[\s-_]/g, '').toLowerCase(),
      domainExtension: `.${spacedExtMatch[2].toLowerCase()}`,
      isDomainFormat: true
    };
  }

  return {
    originalInput: trimmed,
    cleanedBrandName: trimmed,
    isDomainFormat: false
  };
}

export interface CandidateEvaluation {
  id: string;
  rawName: string;
  brandName: string;
  domainString?: string;
  domainExtension?: string;
  isDomain: boolean;

  // Chaldean Calculations
  chaldeanCompound: number;
  chaldeanSingle: number;
  chaldeanBreakdown: LetterBreakdown[];
  chaldeanCompoundInfo: CompoundNumberData;

  // Pythagorean Calculations
  pythagoreanCompound: number;
  pythagoreanSingle: number;
  pythagoreanBreakdown: LetterBreakdown[];

  // Full Domain String Calculations (when domain provided)
  domainStringCompound?: number;
  domainStringSingle?: number;

  // Practical / Brand Linguistic Analytics (Non-Numerological)
  brandAnalytics: {
    pronunciationScore: number; // 0-100
    spellingSimplicityScore: number; // 0-100
    memorabilityScore: number; // 0-100
    searchabilityScore: number; // 0-100
    culturalFit: 'EXCELLENT' | 'GOOD' | 'MODERATE' | 'POOR';
    astrologyRelevance: 'VERY_HIGH' | 'HIGH' | 'MODERATE' | 'LOW';
    syllableCount: number;
    letterCount: number;
    phoneticSummary: string;
    phoneticSummaryHi: string;
  };

  // Compatibility Scores
  compatibilityWithPurpose: 'EXCELLENT' | 'HIGH' | 'MODERATE' | 'LOW';
  compatibilityWithPurposeScore: number; // 0-100
  compatibilityWithKundali: 'HIGH' | 'MODERATE' | 'LOW' | 'NOT_APPLICABLE';
  compatibilityWithKundaliScore: number; // 0-100
  kundaliSynergyNotes?: string;
  kundaliSynergyNotesHi?: string;

  // Overall Evaluation
  brandSuitability: 'EXCELLENT' | 'GOOD' | 'MODERATE' | 'WEAK';
  overallScore: number; // 0-100
  starRating: number; // 1-5 stars
  verdict: string;
  verdictHi: string;
  recommendationType: 'STRONGLY_FAVORED' | 'FAVORABLE' | 'SUITABLE' | 'NEEDS_TUNING';
}

export interface ComparisonResult {
  system: NumerologySystem;
  purpose: BrandPurpose;
  candidates: CandidateEvaluation[];
  bestOverall: CandidateEvaluation;
  secondBest?: CandidateEvaluation;
  thirdBest?: CandidateEvaluation;
  whyBest: string;
  whyBestHi: string;
  hasKundaliIntegrated: boolean;
  disclaimer: string;
  disclaimerHi: string;
}

// Pronunciation and Linguistic Analyzer
function analyzeBrandLinguistics(name: string, purpose: BrandPurpose) {
  const clean = name.trim();
  const lettersOnly = clean.replace(/[^a-zA-Z]/g, '');
  const letterCount = lettersOnly.length;
  
  // Estimate syllables
  const vowels = lettersOnly.toLowerCase().match(/[aeiouy]{1,2}/g);
  const syllableCount = vowels ? vowels.length : 1;

  // Check astrology keywords
  const lower = clean.toLowerCase();
  const hasAstroKeyword = /vedang|jyotish|astro|kundali|nakshatra|horo|rashi|graha|vedic|shastra/i.test(lower);
  const astroRelevance = hasAstroKeyword 
    ? (lower.includes('vedang') && lower.includes('jyotish') ? 'VERY_HIGH' : 'HIGH')
    : (['ASTROLOGY_APP', 'ASTROLOGY_PRACTICE'].includes(purpose) ? 'MODERATE' : 'LOW');

  // Spelling simplicity & pronunciation
  let spellingScore = 85;
  if (letterCount > 18) spellingScore -= 20;
  else if (letterCount > 14) spellingScore -= 10;
  if (/[bcdfghjklmnpqrstvwxyz]{4,}/i.test(lettersOnly)) spellingScore -= 15; // consonant clusters

  let pronunciationScore = 90;
  if (syllableCount > 6) pronunciationScore -= 20;
  else if (syllableCount > 4) pronunciationScore -= 10;

  // Memorability
  let memorabilityScore = 80;
  if (hasAstroKeyword) memorabilityScore += 10;
  if (letterCount >= 8 && letterCount <= 16) memorabilityScore += 5;

  // Searchability
  let searchabilityScore = 85;
  if (clean.includes(' ')) searchabilityScore -= 5;
  if (letterCount < 20) searchabilityScore += 5;

  const phoneticSummary = `Flows with ${syllableCount} cadence beats (${letterCount} glyphs). ${
    hasAstroKeyword ? 'Direct classical Vedic resonance with high semantic recognition.' : 'Modern accessible phonetics.'
  }`;

  const phoneticSummaryHi = `${syllableCount} लयबद्ध शब्दांश (${letterCount} वर्ण)। ${
    hasAstroKeyword ? 'शास्त्रीय वैदिक पहचान व प्रामाणिक ध्वनि प्रवाह।' : 'आधुनिक व सुगम उच्चारण प्रवाह।'
  }`;

  return {
    pronunciationScore: Math.min(100, Math.max(40, pronunciationScore)),
    spellingSimplicityScore: Math.min(100, Math.max(40, spellingScore)),
    memorabilityScore: Math.min(100, Math.max(40, memorabilityScore)),
    searchabilityScore: Math.min(100, Math.max(40, searchabilityScore)),
    culturalFit: (hasAstroKeyword ? 'EXCELLENT' : 'GOOD') as any,
    astrologyRelevance: astroRelevance as any,
    syllableCount,
    letterCount,
    phoneticSummary,
    phoneticSummaryHi
  };
}

// Purpose Alignment Helper
function evaluatePurposeAlignment(singleDigit: number, compound: number, purpose: BrandPurpose): { score: number; label: 'EXCELLENT' | 'HIGH' | 'MODERATE' | 'LOW' } {
  // Planetary rulers for numbers:
  // 1 = Sun (Leadership, Tech flagship, Royal)
  // 2 = Moon (Artistic, Psychology, Maternal)
  // 3 = Jupiter (Astrology, Wisdom, Academics, Guru)
  // 4 = Rahu (Tech, Strategy, Unconventional)
  // 5 = Mercury (Apps, Commerce, Speed, Media, Communication)
  // 6 = Venus (Luxury, Beauty, Harmonious Apps, Design)
  // 7 = Ketu (Deep Research, Occult, Mysticism)
  // 8 = Saturn (Heavy Industry, Long-term Asset, Karma)
  // 9 = Mars (Action, Energy, Defense, Sports)

  let score = 75;

  // Master favorable compounds
  if ([23, 33, 37, 41, 15, 19, 21, 32, 51].includes(compound)) {
    score += 15;
  } else if ([16, 18, 29].includes(compound)) {
    score -= 20;
  }

  if (purpose === 'ASTROLOGY_APP') {
    // Highly rewards Mercury (5) for Apps, Jupiter (3) for Astrology, Venus (6) for UX/App, Sun (1) for Authority
    if ([5, 3, 1, 6].includes(singleDigit)) score += 12;
    if (compound === 23 || compound === 33 || compound === 37) score += 10;
  } else if (purpose === 'ASTROLOGY_PRACTICE') {
    // Rewards Jupiter (3), Sun (1), Ketu (7), Venus (6)
    if ([3, 1, 7, 6].includes(singleDigit)) score += 12;
  } else if (purpose === 'PERSONAL_BRAND') {
    // Rewards Sun (1), Venus (6), Jupiter (3), Mercury (5)
    if ([1, 6, 3, 5].includes(singleDigit)) score += 10;
  } else if (purpose === 'BUSINESS' || purpose === 'COMPANY') {
    // Rewards Mercury (5), Sun (1), Venus (6), Saturn (8 with care)
    if ([5, 1, 6].includes(singleDigit)) score += 12;
  } else if (purpose === 'DOMAIN' || purpose === 'WEBSITE') {
    // Rewards Mercury (5), Sun (1), Venus (6)
    if ([5, 1, 6, 3].includes(singleDigit)) score += 10;
  }

  score = Math.min(98, Math.max(45, score));
  let label: 'EXCELLENT' | 'HIGH' | 'MODERATE' | 'LOW' = 'MODERATE';
  if (score >= 88) label = 'EXCELLENT';
  else if (score >= 75) label = 'HIGH';
  else if (score >= 60) label = 'MODERATE';
  else label = 'LOW';

  return { score, label };
}

// Kundali & Astrological Alignment Helper
function evaluateKundaliSynergy(
  singleDigit: number,
  kundali?: CompleteKundali | null,
  ownerBirthNumber?: number,
  ownerDestinyNumber?: number
) {
  if (!kundali && !ownerBirthNumber && !ownerDestinyNumber) {
    return {
      score: 75,
      compatibility: 'NOT_APPLICABLE' as const,
      notes: 'No birth chart or owner birth dates provided. Universal auspicious vibrations applied.',
      notesHi: 'कोई जन्म विवरण प्रदान नहीं किया गया। सार्वभौमिक शुभ अंक सिद्धांतों का उपयोग किया गया है।'
    };
  }

  let score = 75;
  const reasons: string[] = [];
  const reasonsHi: string[] = [];

  // If birth numbers supplied
  if (ownerBirthNumber) {
    const mRelations = NUMBER_RELATIONS[ownerBirthNumber];
    if (mRelations?.friends.includes(singleDigit)) {
      score += 10;
      reasons.push(`Harmonious with Owner Birth Number (${ownerBirthNumber})`);
      reasonsHi.push(`स्वामी के मूलांक (${ownerBirthNumber}) के साथ अति-मित्रता`);
    } else if (mRelations?.enemies.includes(singleDigit)) {
      score -= 10;
      reasons.push(`Inharmonious with Owner Birth Number (${ownerBirthNumber})`);
      reasonsHi.push(`स्वामी के मूलांक (${ownerBirthNumber}) के साथ शत्रुता`);
    }
  }

  if (ownerDestinyNumber) {
    const dRelations = NUMBER_RELATIONS[ownerDestinyNumber];
    if (dRelations?.friends.includes(singleDigit)) {
      score += 8;
      reasons.push(`Reinforces Life Path / Destiny Number (${ownerDestinyNumber})`);
      reasonsHi.push(`भाग्यांक (${ownerDestinyNumber}) के साथ शुभ तालमेल`);
    }
  }

  // If full Kundali available
  if (kundali) {
    const lagnaHouse = kundali.houses?.[0];
    const lagnaSign = lagnaHouse?.sign || '';
    const lagnaLord = lagnaHouse?.signLord || '';
    const moonPlanet = kundali.planets?.find((p) => p.name === 'Moon');
    const moonSign = moonPlanet?.sign || '';
    const planetOfName = NUMBER_LORDS[singleDigit]?.en.split(' ')[0] || '';

    // Check if name lord is Lagna Lord or Yogakaraka
    if (lagnaLord.toLowerCase().includes(planetOfName.toLowerCase()) || planetOfName.toLowerCase().includes(lagnaLord.toLowerCase())) {
      score += 12;
      reasons.push(`Direct resonance with Lagna Lord (${lagnaLord})`);
      reasonsHi.push(`लग्न स्वामी (${lagnaLord}) के साथ साक्षात तादात्म्य`);
    }

    // Jupiter/Mercury bonus for Astrology
    if (singleDigit === 3 || singleDigit === 5) {
      score += 5;
      reasons.push(`Energizes 5th/9th/10th intellectual houses`);
      reasonsHi.push(`पंचम, नवम व दशम विद्या भावों को बल प्रदान करता है`);
    }
  }

  score = Math.min(99, Math.max(40, score));
  let compatibility: 'HIGH' | 'MODERATE' | 'LOW' = 'MODERATE';
  if (score >= 85) compatibility = 'HIGH';
  else if (score >= 65) compatibility = 'MODERATE';
  else compatibility = 'LOW';

  return {
    score,
    compatibility,
    notes: reasons.length > 0 ? reasons.join(' • ') : 'Neutral alignment with natal chart energetics.',
    notesHi: reasonsHi.length > 0 ? reasonsHi.join(' • ') : 'जन्म पत्रिका की ऊर्जा के साथ तटस्थ व संतुलित तालमेल।'
  };
}

// Single candidate evaluator
export function evaluateNameCandidate(
  input: string,
  system: NumerologySystem = 'CHALDEAN',
  purpose: BrandPurpose = 'ASTROLOGY_APP',
  kundali?: CompleteKundali | null,
  ownerBirthNumber?: number,
  ownerDestinyNumber?: number
): CandidateEvaluation {
  const decomp = parseDomainOrBrand(input);
  const brandName = decomp.cleanedBrandName;

  // Chaldean calculation on Brand Name
  const cleanChaldean = brandName.toUpperCase().replace(/[^A-Z]/g, '');
  let chaldeanCompound = 0;
  const chaldeanBreakdown: LetterBreakdown[] = [];
  for (const ch of cleanChaldean) {
    const val = CHALDEAN_MAP[ch] || 0;
    chaldeanCompound += val;
    chaldeanBreakdown.push({ char: ch, value: val });
  }
  const chaldeanSingle = reduceToSingleDigit(chaldeanCompound);
  const chaldeanCompoundInfo = getCompoundNumberData(chaldeanCompound);

  // Pythagorean calculation on Brand Name
  const cleanPythagorean = brandName.toUpperCase().replace(/[^A-Z]/g, '');
  let pythagoreanCompound = 0;
  const pythagoreanBreakdown: LetterBreakdown[] = [];
  for (const ch of cleanPythagorean) {
    const val = PYTHAGOREAN_MAP[ch] || 0;
    pythagoreanCompound += val;
    pythagoreanBreakdown.push({ char: ch, value: val });
  }
  const pythagoreanSingle = reduceToSingleDigit(pythagoreanCompound);

  // Full domain string calculation if domain
  let domainStringCompound: number | undefined;
  let domainStringSingle: number | undefined;
  if (decomp.isDomainFormat && decomp.domainString) {
    let dComp = 0;
    const cleanDomain = decomp.domainString.toUpperCase().replace(/[^A-Z]/g, '');
    for (const ch of cleanDomain) {
      dComp += system === 'CHALDEAN' ? (CHALDEAN_MAP[ch] || 0) : (PYTHAGOREAN_MAP[ch] || 0);
    }
    domainStringCompound = dComp;
    domainStringSingle = reduceToSingleDigit(dComp);
  }

  // Linguistics & Brand Analytics
  const brandAnalytics = analyzeBrandLinguistics(brandName, purpose);

  // Active single digit for chosen system
  const activeSingle = system === 'CHALDEAN' ? chaldeanSingle : pythagoreanSingle;
  const activeCompound = system === 'CHALDEAN' ? chaldeanCompound : pythagoreanCompound;

  // Purpose compatibility
  const purposeEval = evaluatePurposeAlignment(activeSingle, activeCompound, purpose);

  // Kundali / Birth chart compatibility
  const kundaliEval = evaluateKundaliSynergy(activeSingle, kundali, ownerBirthNumber, ownerDestinyNumber);

  // Weighted overall calculation
  // 35% Numerology Compound & Single Vibration
  // 30% Brand Linguistics / Memorability / Practical
  // 20% Purpose Alignment
  // 15% Kundali / Personal Chart Synergy
  const numScore = (chaldeanCompoundInfo.nature === 'HIGHLY_AUSPICIOUS' ? 95 : chaldeanCompoundInfo.nature === 'AUSPICIOUS' ? 85 : chaldeanCompoundInfo.nature === 'NEUTRAL' ? 70 : 50);
  const practicalScore = (brandAnalytics.pronunciationScore + brandAnalytics.memorabilityScore + brandAnalytics.spellingSimplicityScore) / 3;

  const weightedTotal = Math.round(
    numScore * 0.35 +
    practicalScore * 0.30 +
    purposeEval.score * 0.20 +
    kundaliEval.score * 0.15
  );

  const finalScore = Math.min(99, Math.max(45, weightedTotal));

  let starRating = 3;
  if (finalScore >= 90) starRating = 5;
  else if (finalScore >= 80) starRating = 4;
  else if (finalScore >= 68) starRating = 3;
  else if (finalScore >= 55) starRating = 2;
  else starRating = 1;

  let brandSuitability: 'EXCELLENT' | 'GOOD' | 'MODERATE' | 'WEAK' = 'MODERATE';
  if (finalScore >= 88) brandSuitability = 'EXCELLENT';
  else if (finalScore >= 76) brandSuitability = 'GOOD';
  else if (finalScore >= 62) brandSuitability = 'MODERATE';
  else brandSuitability = 'WEAK';

  let recommendationType: 'STRONGLY_FAVORED' | 'FAVORABLE' | 'SUITABLE' | 'NEEDS_TUNING' = 'SUITABLE';
  if (finalScore >= 88) recommendationType = 'STRONGLY_FAVORED';
  else if (finalScore >= 76) recommendationType = 'FAVORABLE';
  else if (finalScore >= 62) recommendationType = 'SUITABLE';
  else recommendationType = 'NEEDS_TUNING';

  const verdict = `${brandSuitability} alignment: Name vibration ${activeCompound}/${activeSingle} brings ${chaldeanCompoundInfo.symbolName}. Highly suitable for ${purpose.replace(/_/g, ' ').toLowerCase()}.`;
  const verdictHi = `${brandSuitability === 'EXCELLENT' ? 'अति-उत्कृष्ट' : brandSuitability === 'GOOD' ? 'उत्तम' : 'संतुलित'} तालमेल: नामांक ${activeCompound}/${activeSingle} (${chaldeanCompoundInfo.symbolNameHi})। ${purpose.replace(/_/g, ' ')} हेतु अति शुभ।`;

  return {
    id: `CAND_${Math.random().toString(36).substring(2, 8)}`,
    rawName: input,
    brandName,
    domainString: decomp.domainString,
    domainExtension: decomp.domainExtension,
    isDomain: decomp.isDomainFormat,
    chaldeanCompound,
    chaldeanSingle,
    chaldeanBreakdown,
    chaldeanCompoundInfo,
    pythagoreanCompound,
    pythagoreanSingle,
    pythagoreanBreakdown,
    domainStringCompound,
    domainStringSingle,
    brandAnalytics,
    compatibilityWithPurpose: purposeEval.label,
    compatibilityWithPurposeScore: purposeEval.score,
    compatibilityWithKundali: kundaliEval.compatibility,
    compatibilityWithKundaliScore: kundaliEval.score,
    kundaliSynergyNotes: kundaliEval.notes,
    kundaliSynergyNotesHi: kundaliEval.notesHi,
    brandSuitability,
    overallScore: finalScore,
    starRating,
    verdict,
    verdictHi,
    recommendationType
  };
}

// Master multi-candidate comparison engine
export function compareNameCandidates(
  names: string[],
  system: NumerologySystem = 'CHALDEAN',
  purpose: BrandPurpose = 'ASTROLOGY_APP',
  kundali?: CompleteKundali | null,
  ownerBirthNumber?: number,
  ownerDestinyNumber?: number
): ComparisonResult {
  const filteredNames = (names || []).map(n => n.trim()).filter(n => n.length > 0);
  const candidatePool = filteredNames.length > 0 ? filteredNames : ['Vedanga Jyotisha', 'Vedanga Jyotisham', 'Vedang Jyotish', 'Vedang Jyotisham'];

  const evaluations: CandidateEvaluation[] = candidatePool.map(name =>
    evaluateNameCandidate(name, system, purpose, kundali, ownerBirthNumber, ownerDestinyNumber)
  );

  // Sort by overall score descending
  evaluations.sort((a, b) => b.overallScore - a.overallScore);

  const bestOverall = evaluations[0];
  const secondBest = evaluations.length > 1 ? evaluations[1] : undefined;
  const thirdBest = evaluations.length > 2 ? evaluations[2] : undefined;

  // Synthesize concise balanced reasoning for best name
  const whyBest = `"${bestOverall.brandName}" emerges as the premier candidate with an overall score of ${bestOverall.overallScore}/100 (★${bestOverall.starRating}). Its ${system.toLowerCase()} compound vibration ${bestOverall.chaldeanCompound} (${bestOverall.chaldeanCompoundInfo.symbolName}) creates an ideal commercial and linguistic balance for ${purpose.replace(/_/g, ' ').toLowerCase()}, combining high memorability with classical dignity.`;

  const whyBestHi = `"${bestOverall.brandName}" को ${bestOverall.overallScore}/100 (★${bestOverall.starRating}) के साथ सर्वोत्कृष्ट नाम चुना गया है। इसका चैल्डियन संयुक्त अंक ${bestOverall.chaldeanCompound} (${bestOverall.chaldeanCompoundInfo.symbolNameHi}) उच्च संस्मरण क्षमता और शास्त्रीय प्रामाणिकता के साथ ${purpose.replace(/_/g, ' ')} हेतु सर्वाधिक फलदायी है।`;

  return {
    system,
    purpose,
    candidates: evaluations,
    bestOverall,
    secondBest,
    thirdBest,
    whyBest,
    whyBestHi,
    hasKundaliIntegrated: !!kundali || !!ownerBirthNumber,
    disclaimer: 'Numerology recommendations are purely advisory and qualitative. A favorable name vibration enhances resonance and harmony, but does not substitute for sound business execution or ethical diligence.',
    disclaimerHi: 'अंक ज्योतिषीय नाम विश्लेषण विशुद्ध रूप से मार्गदर्शक व सलाहकार है। शुभ नामांक ऊर्जा सामंजस्य को बढ़ाता है, किंतु यह व्यावहारिक कर्मठता व पुरुषार्थ का विकल्प नहीं है।'
  };
}

// Muhurta + Name Registration Advisor Helper (Section 80)
export interface RegistrationMuhurta {
  recommendedDays: string[];
  recommendedDaysHi: string[];
  favorableTithis: string[];
  favorableTithisHi: string[];
  favorableNakshatras: string[];
  favorableNakshatrasHi: string[];
  auspiciousHoraLords: string[];
  guidelines: string;
  guidelinesHi: string;
}

export function getRegistrationMuhurtaRecommendations(candidate: CandidateEvaluation): RegistrationMuhurta {
  const single = candidate.chaldeanSingle;
  const lord = NUMBER_LORDS[single]?.en.split(' ')[0] || 'Mercury';

  const dayMap: Record<number, { en: string[]; hi: string[] }> = {
    1: { en: ['Sunday', 'Thursday'], hi: ['रविवार', 'गुरुवार'] },
    2: { en: ['Monday', 'Sunday'], hi: ['सोमवार', 'रविवार'] },
    3: { en: ['Thursday', 'Tuesday'], hi: ['गुरुवार', 'मंगलवार'] },
    4: { en: ['Saturday', 'Wednesday'], hi: ['शनिवार', 'बुधवार'] },
    5: { en: ['Wednesday', 'Friday'], hi: ['बुधवार', 'शुक्रवार'] },
    6: { en: ['Friday', 'Wednesday'], hi: ['शुक्रवार', 'बुधवार'] },
    7: { en: ['Thursday', 'Sunday'], hi: ['गुरुवार', 'रविवार'] },
    8: { en: ['Saturday', 'Friday'], hi: ['शनिवार', 'शुक्रवार'] },
    9: { en: ['Tuesday', 'Thursday'], hi: ['मंगलवार', 'गुरुवार'] }
  };

  return {
    recommendedDays: dayMap[single]?.en || ['Wednesday', 'Thursday', 'Friday'],
    recommendedDaysHi: dayMap[single]?.hi || ['बुधवार', 'गुरुवार', 'शुक्रवार'],
    favorableTithis: ['Dwitiya (2nd)', 'Tritiya (3rd)', 'Panchami (5th)', 'Saptami (7th)', 'Dashami (10th)', 'Ekadashi (11th)', 'Trayodashi (13th)'],
    favorableTithisHi: ['द्वितीया', 'तृतीया', 'पंचमी', 'सप्तमी', 'दशमी', 'एकादशी', 'त्रयोदशी (शुक्ल पक्ष)'],
    favorableNakshatras: ['Pushya', 'Rohini', 'Ashwini', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Uttara Phalguni', 'Revati'],
    favorableNakshatrasHi: ['पुष्य', 'रोहिणी', 'अश्विनी', 'हस्त', 'चित्रा', 'स्वाति', 'अनुराधा', 'उत्तराफाल्गुनी', 'रेवती'],
    auspiciousHoraLords: [lord, 'Jupiter (Guru)', 'Mercury (Budha)', 'Venus (Shukra)'],
    guidelines: `When registering "${candidate.brandName}" (Domain, Trademark, or App Store), prefer the waxing Moon (Shukla Paksha) during the auspicious Hora of ${lord} or Guru. Avoid Rahu Kalam.`,
    guidelinesHi: `"${candidate.brandName}" के डोमेन, ट्रेडमार्क अथवा ऐप पंजीकरण के लिए शुक्ल पक्ष में ${lord} अथवा गुरु की शुभ होरा का चयन करें। राहुकाल से बचें।`
  };
}

// Name Change / Brand Migration Evaluator (Section 81)
export interface NameChangeAssessment {
  currentCandidate: CandidateEvaluation;
  proposedCandidate: CandidateEvaluation;
  isFavorableShift: boolean;
  scoreDifference: number; // positive = better
  summary: string;
  summaryHi: string;
  keyBenefits: string[];
  keyBenefitsHi: string[];
  risks: string[];
  risksHi: string[];
}

export function evaluateNameChange(
  currentName: string,
  proposedName: string,
  system: NumerologySystem = 'CHALDEAN',
  purpose: BrandPurpose = 'ASTROLOGY_APP',
  kundali?: CompleteKundali | null
): NameChangeAssessment {
  const current = evaluateNameCandidate(currentName, system, purpose, kundali);
  const proposed = evaluateNameCandidate(proposedName, system, purpose, kundali);

  const scoreDiff = proposed.overallScore - current.overallScore;
  const isFavorableShift = scoreDiff >= 0;

  const keyBenefits: string[] = [];
  const keyBenefitsHi: string[] = [];
  const risks: string[] = [];
  const risksHi: string[] = [];

  if (proposed.overallScore > current.overallScore) {
    keyBenefits.push(`Elevates overall vibrational harmony by +${scoreDiff} points`);
    keyBenefitsHi.push(`समग्र नामांक सामंजस्य में +${scoreDiff} अंकों की वृद्धि`);
  }
  if (proposed.chaldeanCompoundInfo.nature === 'HIGHLY_AUSPICIOUS' && current.chaldeanCompoundInfo.nature !== 'HIGHLY_AUSPICIOUS') {
    keyBenefits.push(`Upgrades compound number to ${proposed.chaldeanCompound} (${proposed.chaldeanCompoundInfo.symbolName})`);
    keyBenefitsHi.push(`संयुक्त अंक को ${proposed.chaldeanCompound} (${proposed.chaldeanCompoundInfo.symbolNameHi}) में उन्नत करता है`);
  }
  if (proposed.brandAnalytics.memorabilityScore > current.brandAnalytics.memorabilityScore) {
    keyBenefits.push(`Sharper phonetic memorability and search simplicity`);
    keyBenefitsHi.push(`सुगम उच्चारण और तीव्र संस्मरण क्षमता`);
  }

  if (scoreDiff < 0) {
    risks.push(`Reduces composite alignment score by ${Math.abs(scoreDiff)} points`);
    risksHi.push(`समग्र अनुकूलता स्कोर में ${Math.abs(scoreDiff)} अंकों की गिरावट`);
  }
  risks.push(`Requires updating existing brand recognition, search indexing, and domain redirects`);
  risksHi.push(`पूर्व स्थापित पहचान, सर्च इंडेक्सिंग और डोमेन रीडायरेक्ट को अद्यतन करने की आवश्यकता`);

  const summary = isFavorableShift
    ? `Migrating from "${currentName}" (${current.overallScore}/100) to "${proposedName}" (${proposed.overallScore}/100) is numerically favourable and strengthens commercial appeal.`
    : `Retaining "${currentName}" (${current.overallScore}/100) is preferable over "${proposedName}" (${proposed.overallScore}/100). The current name maintains stronger natural resonance.`;

  const summaryHi = isFavorableShift
    ? `"${currentName}" (${current.overallScore}/100) से "${proposedName}" (${proposed.overallScore}/100) पर जाना अंक ज्योतिषीय दृष्टि से शुभ व उन्नति कारक है।`
    : `"${currentName}" (${current.overallScore}/100) को बनाए रखना "${proposedName}" (${proposed.overallScore}/100) की तुलना में अधिक श्रेष्ठ व प्रभावशाली है।`;

  return {
    currentCandidate: current,
    proposedCandidate: proposed,
    isFavorableShift,
    scoreDifference: scoreDiff,
    summary,
    summaryHi,
    keyBenefits,
    keyBenefitsHi,
    risks,
    risksHi
  };
}
